import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import DatePicker from 'components/DatePicker';
import Checkbox from 'components/DesignSystem/Checkbox';
import Icon from 'components/DesignSystem/Icon';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import SavePreferencesModal from 'components/Meteorology/Fields/ControlPanel/NumericForecast/SavePreferencesModal';
import ConfirmationModal from 'components/PreferenceDialog/Success';
import ErrorModal from 'components/PreferenceDialog/Error';
import useNumericForecastControlPanel from 'utils/hooks/meteorology/useNumericForecastControlPanel';
import useControlPanelOptions from 'utils/hooks/meteorology/useControlPanelOptions';
import useVariables from 'hooks/Meteorology/Fields/useVariables';
import useSavePreferencesModal from 'utils/hooks/meteorology/useSavePreferencesModal';
import useLoadFirstPreference from 'hooks/Meteorology/Fields/NumericForecast/useLoadFirstPreference';
import UseSetNumericForecastDefaults from 'components/Meteorology/Fields/ControlPanel/NumericForecast/UseSetNumericForecastDefaults';
import useFromUnixTime from 'utils/hooks/useFromUnixTime';
import { useHandleRequestErrors } from 'utils/hooks';
import {
  useListPntPreferencesQuery,
  useDeletePntPreferencesMutation,
  PreferenceList,
} from 'services/api/Meteorology';
import { ResponseError } from 'services/api/base';
import { isHeatmapVariable } from 'helper/products/meteorology/heatmapModels';
import dateDiffToDate from 'helper/dateDiffToDate';
import {
  requestConsult,
  selectMFControlPanelViewMode,
  setViewMode,
} from 'redux/reducers/meteorologyFieldsControlPanelReducer';
import { cssVar } from 'constants/theme';
import { REGIONS, PERIODS } from '../filterOptions';

import './index.scss';

type SheetSection = 'preferences' | 'variable' | 'viewmode' | 'models' | 'date' | 'region' | 'period' | '';

export default function ControlPanelSheet({
  open,
  dismiss,
}: {
  open: boolean;
  dismiss: () => void;
}) {
  const dispatch = useDispatch();
  const viewMode = useSelector(selectMFControlPanelViewMode);
  const [openSection, setOpenSection] = useState<SheetSection>('');
  const toggleSection = (section: SheetSection) => () => (
    setOpenSection((current) => (current === section ? '' : section))
  );

  const { models, getModelOptions, isFetching } = useControlPanelOptions('pnt');
  const { variables, selectableModels } = useVariables(models, getModelOptions, viewMode);
  const {
    setShowFormModal,
    isShowingConfirmationModal,
    setShowConfirmationModal,
    isShowingErrorModal,
    setShowErrorModal,
    error: saveError,
  } = useSavePreferencesModal();
  const { data, error } = useListPntPreferencesQuery();
  const preferenceList = data?.data || [];
  const [onPreferenceDeleteTrigger, { error: preferenceDeleteError }] = (
    useDeletePntPreferencesMutation()
  );
  useHandleRequestErrors(error as ResponseError | undefined);
  useHandleRequestErrors(preferenceDeleteError as ResponseError | undefined);

  const {
    region,
    models: selectedModels,
    period,
    variable,
    forecastUnixDate,
    setForecastUnixDate,
    setPeriod,
    setModelArray,
    setRegion,
    anomaly,
    setAnomaly,
    setVariable,
    resetPreferences,
    controlPanelUsed,
  } = useNumericForecastControlPanel();
  const selectedDate = useFromUnixTime(forecastUnixDate);

  useLoadFirstPreference({
    data,
    preferenceList,
    resetPreferences,
    setPeriod,
    setModelArray,
    setRegion,
    setAnomaly,
    setForecastUnixDate,
    controlPanelUsed,
    setVariable,
    setViewMode: (mode) => dispatch(setViewMode(mode)),
    requestConsult: () => dispatch(requestConsult()),
  });
  UseSetNumericForecastDefaults({
    data,
    preferenceList,
    controlPanelUsed,
    region,
    selectedModels,
    period,
    variable,
    anomaly,
    forecastUnixDate,
    setRegion,
    setModelArray,
    setPeriod,
    setVariable,
    setAnomaly,
    setForecastUnixDate,
    requestConsult: () => dispatch(requestConsult()),
  });

  const loadPreference = (preference: PreferenceList) => {
    const today = new Date();
    const date = dateDiffToDate(preference.json.data_prev, [today]) || today;
    // Preferências salvas antes do Heatmap não têm tipo_visualizacao → 'spatial'.
    const preferenceViewMode = preference.json.tipo_visualizacao ?? 'spatial';

    resetPreferences();
    setPeriod(preference.json.periodo_interesse);
    setVariable(preference.json.variavel_meteorologica);
    setModelArray(preference.json.modelos);
    setRegion(preference.json.area_plotagem);
    setForecastUnixDate(date);
    setAnomaly(preference.json.anom);
    dispatch(setViewMode(preferenceViewMode));

    // Dispara a consulta automaticamente quando a preferência tem os filtros
    // mínimos necessários — assim o usuário não precisa clicar em "Realizar consulta".
    const hasModels = (preference.json.modelos ?? []).length > 0;
    const canQuery = preferenceViewMode === 'heatmap'
      ? Boolean(preference.json.variavel_meteorologica && hasModels)
      : Boolean(
        preference.json.area_plotagem && hasModels && preference.json.periodo_interesse,
      );
    if (canQuery) {
      dispatch(requestConsult());
    }

    // Fecha o painel após aplicar a preferência, mantendo consistência
    // com o comportamento do botão "Realizar consulta"
    dismiss();
  };

  if (isFetching) {
    return (
      <BottomSheet
        open={open}
        dismiss={dismiss}
        title="Painel de controle"
        className="tf-pnt-mobile-control-panel-sheet"
      >
        <div className="tf-pnt-mobile-sheet-loading">
          <Spinner />
        </div>
      </BottomSheet>
    );
  }

  const isHeatmap = viewMode === 'heatmap';
  const isHeatmapDisabled = !variable || !isHeatmapVariable(variable);
  const baseSpatialParamsReady = Boolean(
    region && selectedModels && selectedModels.length > 0 && period && forecastUnixDate,
  );
  const heatmapParamsReady = Boolean(
    variable && forecastUnixDate && selectedModels && selectedModels.length > 0,
  );
  const areParamsSelected = isHeatmap ? heatmapParamsReady : baseSpatialParamsReady;

  const variableLabel = variables.find((v) => v.id === variable)?.label;
  const regionLabel = REGIONS.find((r) => r.value === region)?.label;
  const periodLabel = PERIODS.find((p) => p.value === period)?.label;

  const toggleModel = (value: string) => {
    if (selectedModels.includes(value)) {
      setModelArray(selectedModels.filter((m) => m !== value));
    } else {
      setModelArray(selectedModels.concat(value));
    }
  };

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Painel de controle"
      className="tf-pnt-mobile-control-panel-sheet"
    >
      <SavePreferencesModal />
      <ConfirmationModal
        open={isShowingConfirmationModal}
        closing={() => setShowConfirmationModal(false)}
      />
      <ErrorModal
        open={isShowingErrorModal}
        message={saveError}
        closing={() => setShowErrorModal(false)}
      />
      <div className="tf-pnt-mobile-sheet-body">
        <Section
          label="Preferências salvas"
          open={openSection === 'preferences'}
          onToggle={toggleSection('preferences')}
        >
          {preferenceList.length === 0 && (
            <p className="tf-pnt-mobile-sheet-empty">Nenhuma preferência disponível.</p>
          )}
          {preferenceList.map((preference) => (
            <div key={preference.id} className="tf-pnt-preference-row">
              <button
                type="button"
                className="tf-pnt-preference-row-name"
                onClick={() => loadPreference(preference)}
              >
                {preference.name}
              </button>
              <button
                type="button"
                aria-label="Excluir preferência"
                className="tf-pnt-preference-row-delete"
                onClick={() => onPreferenceDeleteTrigger({ id: preference.id })}
              >
                <Icon iconType="trash" color={cssVar('danger-fg')} size="small" />
              </button>
            </div>
          ))}
        </Section>

        <Section
          label="Variável"
          valueText={variableLabel}
          open={openSection === 'variable'}
          onToggle={toggleSection('variable')}
        >
          {variables.map((v) => (
            <OptionRow
              key={v.id}
              label={v.label}
              selected={v.id === variable}
              onSelect={() => setVariable(v.id)}
            />
          ))}
        </Section>

        <Section
          label="Visualização"
          valueText={isHeatmap ? 'Heatmap' : 'Espacial'}
          open={openSection === 'viewmode'}
          onToggle={toggleSection('viewmode')}
        >
          <OptionRow
            label="Espacial"
            selected={!isHeatmap}
            onSelect={() => dispatch(setViewMode('spatial'))}
          />
          <OptionRow
            label="Heatmap"
            selected={isHeatmap}
            onSelect={() => !isHeatmapDisabled && dispatch(setViewMode('heatmap'))}
          />
          {isHeatmapDisabled && (
            <p className="tf-pnt-mobile-sheet-hint">
              Heatmap disponível apenas para variáveis de temperatura.
            </p>
          )}
        </Section>

        <Section
          label="Modelos"
          valueText={`${selectedModels.length} selecionado(s)`}
          open={openSection === 'models'}
          onToggle={toggleSection('models')}
        >
          {selectableModels.map((m) => (
            <OptionRow
              key={m.value}
              label={m.label}
              selected={selectedModels.includes(m.value)}
              onSelect={() => toggleModel(m.value)}
              selectionType="multiple"
            />
          ))}
          <div className="tf-pnt-mobile-models-actions">
            <button
              type="button"
              className="tf-pnt-mobile-models-action"
              onClick={() => setModelArray(selectableModels.map((m) => m.value))}
            >
              Todos
            </button>
            <button
              type="button"
              className="tf-pnt-mobile-models-action"
              onClick={() => setModelArray([])}
            >
              Nenhum
            </button>
          </div>
        </Section>

        <Section
          label="Data de previsão"
          valueText={selectedDate ? selectedDate.toLocaleDateString('pt-BR') : undefined}
          open={openSection === 'date'}
          onToggle={toggleSection('date')}
        >
          <DatePicker
            inline
            selected={selectedDate}
            onChange={setForecastUnixDate}
            maxDate={new Date()}
          />
        </Section>

        {!isHeatmap && (
          <Section
            label="Área de plotagem"
            valueText={regionLabel}
            open={openSection === 'region'}
            onToggle={toggleSection('region')}
          >
            {REGIONS.map((r) => (
              <OptionRow
                key={r.value}
                label={r.label}
                selected={r.value === region}
                onSelect={() => setRegion(r.value)}
              />
            ))}
          </Section>
        )}

        {!isHeatmap && (
          <Section
            label="Período de seleção"
            valueText={periodLabel}
            open={openSection === 'period'}
            onToggle={toggleSection('period')}
          >
            {PERIODS.map((p) => (
              <OptionRow
                key={p.value}
                label={p.label}
                selected={p.value === period}
                onSelect={() => setPeriod(p.value)}
              />
            ))}
          </Section>
        )}

        {!isHeatmap && (
          <Checkbox
            id="pnt-mobile-anomaly-checkbox"
            className="tf-pnt-mobile-sheet-anomaly"
            label="Anomalia"
            checked={anomaly}
            onChange={() => setAnomaly(!anomaly)}
          />
        )}
      </div>

      <div className="tf-pnt-mobile-sheet-footer">
        <SheetActionButton variant="secondary" onClick={() => setShowFormModal(true)}>
          Salvar nova preferência
        </SheetActionButton>
        <SheetActionButton
          variant="confirm"
          disabled={!areParamsSelected}
          onClick={() => { dispatch(requestConsult()); dismiss(); }}
        >
          Realizar consulta
        </SheetActionButton>
      </div>
    </BottomSheet>
  );
}
